import { useEffect, useState } from 'react';
import axios from 'axios';
import language from '../../variables/language';

// One composer for both entry points (create window and open constructor):
// screenshot (paste or file) -> local OCR -> editable text -> AI draft ->
// editable questions. It reports the draft upward; the caller decides whether
// to create a new exercise (/materialize) or append to the open one
// (/append-blocks).

const TYPE_LABEL_KEYS = {
   fill_blank: 'typeFillBlank',
   multiple_choice: 'typeMultipleChoice',
   true_false: 'typeTrueFalse',
   ordering: 'typeOrdering',
   matching: 'typeMatching',
   heading: 'typeHeading',
   text: 'typeText',
   list: 'typeList',
   image: 'typeImage',
};

// Same accents the block picker uses, so a draft question looks like the block
// it will become.
const TYPE_ACCENT = {
   heading: '#63dac8',
   text: '#7ec2e8',
   list: '#e0b04c',
   image: '#c77dff',
   fill_blank: '#3ddf93',
   multiple_choice: '#3ddf93',
   true_false: '#3ddf93',
   ordering: '#3ddf93',
   matching: '#3ddf93',
};

// The single text a block type exposes for editing. Matching pairs and list/
// image details are edited in their own inputs below.
function mainTextOf(question) {
   if (question.type === 'fill_blank') return { key: 'template' };
   if (question.type === 'multiple_choice') return { key: 'question' };
   if (question.type === 'true_false') return { key: 'statement' };
   if (question.type === 'ordering') return { key: 'prompt' };
   if (question.type === 'heading' || question.type === 'text') return { key: 'text' };
   return null;
}

function imageFrom(items) {
   const list = Array.from(items || []);
   const item = list.find(i => i.type && i.type.startsWith('image/'));
   return item ? item.getAsFile() : null;
}

export default function DraftComposer({ onDraftChange, initialText = '' }) {
   const lang = language[language.lang];
   const t = lang.screenshotImport;

   const [file, setFile] = useState(null);
   const [previewUrl, setPreviewUrl] = useState('');
   const [ocrStatus, setOcrStatus] = useState('idle');
   const [ocrError, setOcrError] = useState('');
   const [text, setText] = useState(initialText);
   const [blanks, setBlanks] = useState([]);
   const [lowConfidence, setLowConfidence] = useState(false);
   const [instruction, setInstruction] = useState('');

   const [draftName, setDraftName] = useState('');
   const [questions, setQuestions] = useState(null);
   const [genStatus, setGenStatus] = useState('idle');
   const [genError, setGenError] = useState('');

   useEffect(() => {
      if (!file) { setPreviewUrl(''); return undefined; }
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
   }, [file]);

   useEffect(() => {
      if (onDraftChange) {
         onDraftChange(questions && questions.length > 0 ? { exerciseName: draftName, questions } : null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [questions, draftName]);

   // Ctrl+V with a screenshot works anywhere while this composer is on screen,
   // not only when the paste zone itself has focus.
   useEffect(() => {
      const onWindowPaste = (e) => {
         const picked = imageFrom(e.clipboardData && e.clipboardData.items);
         if (picked) {
            e.preventDefault();
            acceptImage(picked);
         }
      };
      window.addEventListener('paste', onWindowPaste);
      return () => window.removeEventListener('paste', onWindowPaste);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const acceptImage = (candidate) => {
      if (!candidate || !candidate.type || !candidate.type.startsWith('image/')) return;
      setFile(candidate);
      setOcrStatus('idle');
      setOcrError('');
   };

   const recognize = () => {
      if (!file || ocrStatus === 'loading') return;
      setOcrStatus('loading');
      setOcrError('');
      const form = new FormData();
      form.append('file', file, file.name || 'screenshot.png');
      axios.post('/api/exercise-ocr/recognize', form, { withCredentials: true })
         .then(response => {
            const data = response.data || {};
            setText(data.text || '');
            setBlanks(Array.isArray(data.blanks) ? data.blanks : []);
            setLowConfidence(!!data.lowConfidence);
            setOcrStatus('done');
         })
         .catch(err => {
            setOcrStatus('error');
            setOcrError(err.response && err.response.status === 503 ? t.ocrUnavailable : t.ocrFailed);
         });
   };

   const generate = () => {
      if (!text.trim() || genStatus === 'loading') return;
      setGenStatus('loading');
      setGenError('');
      const extra = instruction.trim() ? '\n\nInstrukcja nauczyciela: ' + instruction.trim() : '';
      axios.post('/api/exercise-ocr/generate-draft', { text: text + extra, blanks }, { withCredentials: true })
         .then(response => {
            const data = response.data || {};
            setQuestions(Array.isArray(data.questions) ? data.questions : []);
            setDraftName(data.exerciseName || '');
            setGenStatus('done');
         })
         .catch(err => {
            setGenStatus('error');
            setGenError(err.response && err.response.data && err.response.data.error ? err.response.data.error : t.draftFailed);
         });
   };

   const updateQuestion = (index, patch) => {
      setQuestions(prev => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
   };

   const removeQuestion = (index) => {
      setQuestions(prev => prev.filter((_, i) => i !== index));
   };

   const hasDraft = questions !== null;

   return (
      <div className='ecm-composer'>
         <section className='ecm-section' style={{ '--accent': '#c77dff' }}>
            <h4 className='ecm-section-title'>
               <span className='material-symbols-outlined ecm-section-icon'>image</span>
               {t.sectionImage} <span className='ecm-optional'>({t.optional})</span>
            </h4>
            <label className={'ecm-paste-zone' + (previewUrl ? ' has-image' : '')} tabIndex={0}
               onPaste={(e) => { const picked = imageFrom(e.clipboardData && e.clipboardData.items); if (picked) { e.preventDefault(); acceptImage(picked); } }}>
               {previewUrl ? (
                  <img className='ecm-thumb' src={previewUrl} alt='' />
               ) : (
                  <>
                     <span className='material-symbols-outlined ecm-paste-icon'>content_paste</span>
                     <span className='ecm-paste-text'>{t.pasteHint}</span>
                  </>
               )}
               <input type='file' accept='image/*' onChange={e => acceptImage(e.target.files && e.target.files[0])} />
            </label>
            {previewUrl ? (
               <div className='ecm-row-end'>
                  <button type='button' className='ecm-btn' disabled={ocrStatus === 'loading'} onClick={recognize}>
                     <span className='material-symbols-outlined ecm-btn-icon'>document_scanner</span>
                     {ocrStatus === 'loading' ? t.recognizing : t.recognize}
                  </button>
               </div>
            ) : ''}
            {ocrError ? <p className='ecm-error'>{ocrError}</p> : ''}
         </section>

         <section className='ecm-section' style={{ '--accent': '#e0b04c' }}>
            <h4 className='ecm-section-title'>
               <span className='material-symbols-outlined ecm-section-icon'>notes</span>
               {t.sectionText}
            </h4>
            <textarea className='ecm-textarea' rows={6} value={text}
               onChange={e => setText(e.target.value)} placeholder={t.textPlaceholder} />
            <div className='ecm-meta'>
               {ocrStatus === 'done' ? <span className='ecm-chip'>{t.blanksCount}: {blanks.length}</span> : ''}
               {lowConfidence ? <span className='ecm-warn'>{t.lowConfidence}</span> : ''}
            </div>
            <label className='ecm-field'>
               <span>{t.instructionLabel}</span>
               <input type='text' value={instruction} onChange={e => setInstruction(e.target.value)}
                  placeholder={t.instructionPlaceholder} />
            </label>
            <div className='ecm-row-end'>
               <button type='button' className='ecm-btn ecm-btn-primary'
                  disabled={!text.trim() || genStatus === 'loading'} onClick={generate}>
                  <span className='material-symbols-outlined ecm-btn-icon'>auto_awesome</span>
                  {genStatus === 'loading' ? t.generating : (hasDraft ? t.regenerate : t.generate)}
               </button>
            </div>
            {genError ? <p className='ecm-error'>{genError}</p> : ''}
         </section>

         {hasDraft ? (
            <section className='ecm-section' style={{ '--accent': '#3ddf93' }}>
               <h4 className='ecm-section-title'>
                  <span className='material-symbols-outlined ecm-section-icon'>auto_awesome</span>
                  {t.sectionDraft}
               </h4>
               <p className='ecm-hint'>{t.questionsCount}: {questions.length}. {t.editHint}</p>
               <div className='ecm-questions'>
                  {questions.map((q, i) => (
                     <DraftQuestion key={i} question={q}
                        label={lang.screenshotImport[TYPE_LABEL_KEYS[q.type]] || q.type}
                        accent={TYPE_ACCENT[q.type] || '#a2a2a2'}
                        removeLabel={t.removeQuestion}
                        onChange={(patch) => updateQuestion(i, patch)}
                        onRemove={() => removeQuestion(i)} />
                  ))}
               </div>
            </section>
         ) : ''}
      </div>
   );
}

function DraftQuestion({ question, label, accent, removeLabel, onChange, onRemove }) {
   const main = mainTextOf(question);
   return (
      <div className='ecm-question' style={{ '--accent': accent }}>
         <div className='ecm-question-head'>
            <span className='ecm-type'>{label}</span>
            <button type='button' className='ecm-remove' onClick={onRemove}>
               <span className='material-symbols-outlined ecm-btn-icon'>delete</span>
               {removeLabel}
            </button>
         </div>
         {main ? (
            <textarea className='ecm-question-input' rows={2} value={question[main.key] || ''}
               onChange={e => onChange({ [main.key]: e.target.value })} />
         ) : ''}
         {question.type === 'list' ? (
            <textarea className='ecm-question-input' rows={3} value={(question.items || []).join('\n')}
               onChange={e => onChange({ items: e.target.value.split('\n') })} />
         ) : ''}
         {question.type === 'image' ? (
            <>
               <input className='ecm-url-input' type='text' value={question.url || ''} placeholder='https://'
                  onChange={e => onChange({ url: e.target.value })} />
               <input className='ecm-url-input' type='text' value={question.alt || ''} placeholder='alt'
                  onChange={e => onChange({ alt: e.target.value })} />
            </>
         ) : ''}
         {question.type === 'matching' ? (
            <span className='ecm-hint'>{Array.isArray(question.pairs) ? question.pairs.length + ' pairs' : ''}</span>
         ) : ''}
      </div>
   );
}
